import { useState } from "react"
import * as yup from "yup"
import { makeStyles } from "tss-react/mui"
import { faXmark } from "@fortawesome/pro-solid-svg-icons"
import { Form, Formik, useFormikContext } from "formik"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { Box, List, ListItem, Stack } from "@mui/material"

import { useAppLocalization } from "../../localization/components/AppLocalizationProvider"
import { StandardMainPanel } from "../../../shared/components/forms/StandardMainPanel"
import { StandardButton } from "../../../shared/components/buttons/StandardButton"
import { FormikPasswordField } from "../../../shared/components/forms/Formik"
import { getValidatePwRequest, validatePassword } from "../../login/loginApi"
import { StandardForm } from "../../../shared/components/forms/StandardForm"
import { LocalizedStrict } from "../../localization/components/AppLocalized"
import { usePublicApiMutation } from "../../../core/api/useApiMutation"
import { selectAuthenticatedUser } from "../../login/loginSelectors"
import { useAppSelector } from "../../store/storeHooks"

import { ChangePasswordFormValues, ChangePasswordParams } from "../profileTypes"

const useStyles = makeStyles()((theme) => ({
    ruleBox: {
        width: "100%",
        color: theme.palette.tailwind.sky[700],
        backgroundColor: theme.palette.tailwind.sky[50],
        borderRadius: "0.5rem",
        margin: 0,
        padding: "0.5rem 0",
    },
    listItem: {
        display: "flex",
        gap: "0.4rem",
        alignItems: "flex-start",
        fontSize: "0.875rem",
    },
    icon: {
        color: "#D32F2F",
        display: "block",
        paddingTop: "0.25rem",
    },
}))

type ChangePasswordFormProps = Readonly<{
    onSubmit: (formData: ChangePasswordParams) => void
}>

export const ChangePasswordForm = ({ onSubmit }: ChangePasswordFormProps) => {
    const { l10n } = useAppLocalization()
    const user = useAppSelector(selectAuthenticatedUser)
    const [pwErrors, setPwErrors] = useState<string[]>([])

    const { mutateAsync: callValidatePassword } = usePublicApiMutation(validatePassword, {})

    const initialValues = {
        currentPassword: "",
        newPassword: "",
        confirmPassword: "",
    }

    const validationSchema = yup.object({
        currentPassword: yup.string().required(l10n.getString("required-field")),
        newPassword: yup
            .string()
            .required(l10n.getString("required-field"))
            .test({
                name: "is-pw-valid",
                test: async (value, _context) => {
                    if (!user) return true
                    const request = getValidatePwRequest({
                        userType: user.userType,
                        companyId: user.companyId,
                        serviceCenterId: user.serviceCenterId ?? null,
                        userGroupId: user.userGroupId ?? null,
                        password: value,
                    })
                    const response = await callValidatePassword(request)
                    if (response && !response.validator.success) {
                        setPwErrors(response.validator.rules)
                        return false
                    } else {
                        setPwErrors([])
                        return true
                    }
                },
                message: l10n.getString("create-password-password-invalid-for-policy"),
            }),
        confirmPassword: yup
            .string()
            .required(l10n.getString("required-field"))
            .oneOf([yup.ref("newPassword")], l10n.getString("profile-change-password-profile-match-password")),
    })

    function handleSubmit({ currentPassword, newPassword }: ChangePasswordFormValues) {
        onSubmit({
            currentPassword,
            newPassword,
        })
    }

    return (
        <Formik<ChangePasswordFormValues>
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validationSchema}
            onSubmit={handleSubmit}
        >
            <Form>
                <ChangePasswordFormContent pwErrors={pwErrors} />
            </Form>
        </Formik>
    )
}

type ChangePasswordFormContentProps = Readonly<{
    pwErrors: string[]
}>

const ChangePasswordFormContent = ({ pwErrors }: ChangePasswordFormContentProps) => {
    const { classes } = useStyles()
    const { isValid } = useFormikContext<ChangePasswordFormValues>()

    return (
        <StandardForm>
            <StandardMainPanel>
                <Stack>
                    <LocalizedStrict id="profile-change-password-profile-currentPassword" attrs={{ label: true }}>
                        <FormikPasswordField<ChangePasswordFormValues>
                            name="currentPassword"
                            label="Current password"
                            fullWidth
                            margin="normal"
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="profile-change-password-profile-newPassword" attrs={{ label: true }}>
                        <FormikPasswordField<ChangePasswordFormValues>
                            name="newPassword"
                            label="New password"
                            fullWidth
                            margin="normal"
                        />
                    </LocalizedStrict>
                    <LocalizedStrict id="profile-change-password-profile-confirmPassword" attrs={{ label: true }}>
                        <FormikPasswordField<ChangePasswordFormValues>
                            name="confirmPassword"
                            label="Confirm password"
                            fullWidth
                            margin="normal"
                        />
                    </LocalizedStrict>

                    {pwErrors.length > 0 && (
                        <List className={classes.ruleBox}>
                            {pwErrors.map((message) => (
                                <ListItem key={message} className={classes.listItem}>
                                    <FontAwesomeIcon icon={faXmark} size="sm" className={classes.icon} />
                                    {message}
                                </ListItem>
                            ))}
                        </List>
                    )}
                </Stack>
                <Box>
                    <LocalizedStrict id="profile-change-password-submit-button">
                        <StandardButton type="submit" fullWidth={false} variant="contained" disabled={!isValid}>
                            Update password
                        </StandardButton>
                    </LocalizedStrict>
                </Box>
            </StandardMainPanel>
        </StandardForm>
    )
}
